// Onboarding-wizard completeness helpers. Pure module — no firebase/store
// imports, so it is safely importable from mobile screens, the navigator,
// web columns and common actions alike.
//
// MUST stay in sync with functions/common/onboarding.js (CommonJS twin used
// by the complete_onboarding Cloud Function). Fixtures in
// functions/test/onboarding.spec.js run against BOTH files.
//
// Everything on /users/{uid} is client-writable, so these predicates gate
// UX only (which screens to show). Money decisions live server-side and use
// Firebase Auth metadata + the server-only /driver_welcome_credits ledger.

export const PLACEHOLDER_NAMES = ['manual', 'customer', 'rider', 'driver', 'user', 'test'];
// Latin (incl. accented), Ethiopic and Arabic letters, then letters/space/.'-,
// 2..50 chars. Explicit code-point ranges, no `u` flag: the functions lint
// config is ES2017 and rejects unicode property escapes.
export const NAME_RE = /^[A-Za-zÀ-ɏሀ-፿؀-ۿ][A-Za-zÀ-ɏሀ-፿؀-ۿ\s.'-]{1,49}$/;
export const TIN_RE = /^\d{10}$/;
export const PLATE_EXAMPLE = '3 AA 12345';
export const EMAIL_DOMAIN = 'donkeydrive.io';

const str = (v) => (v === undefined || v === null) ? '' : String(v);
const trim = (v) => str(v).trim();

export const isRealName = (s) => {
    const t = trim(s);
    if (!NAME_RE.test(t)) return false;
    return PLACEHOLDER_NAMES.indexOf(t.toLowerCase()) < 0;
};

export const hasRealName = (p) => Boolean(p) && isRealName(p.firstName) && isRealName(p.lastName);

export const normalizeTin = (t) => str(t).replace(/\D/g, '');

// Ethiopian TIN = 10 digits. Reject the obvious junk people type to get past
// a required field (all one digit, ascending/descending runs).
export const isValidTin = (t) => {
    const d = normalizeTin(t);
    if (!TIN_RE.test(d)) return false;
    if (/^(\d)\1{9}$/.test(d)) return false;
    if (d === '0123456789' || d === '1234567890' || d === '9876543210') return false;
    return true;
};

export const normalizePlate = (p) => trim(p).toUpperCase().replace(/\s+/g, ' ');

// Plate: NOT EMPTY, nothing else (owner decision 2026-09-05 — drivers type
// "AA 13456", "3 AA 123456" and other regional forms, so no format rule;
// staff verify the plate against the ownership certificate). Same rule in
// the CJS twin and in complete_onboarding.
export const isValidPlate = (p) => normalizePlate(p).length > 0;

// Email local part: ASCII slug of first.last; Ethiopic/Arabic names slug to
// '' and fall through to the verified phone digits (the pattern already used
// for OTP customers), so the preview shown in the wizard always matches what
// the server will bind (modulo a numeric suffix on collision).
export const slugName = (s) => trim(s).normalize('NFKD').toLowerCase().replace(/[^a-z0-9]/g, '');

export const buildEmailLocalPart = (first, last, phoneDigits) => {
    const f = slugName(first);
    const l = slugName(last);
    if (f && l) return f + '.' + l;
    if (f || l) return f || l;
    return str(phoneDigits).replace(/\D/g, '');
};

export const buildEmailPreview = (first, last, mobile) => {
    const local = buildEmailLocalPart(first, last, mobile);
    return local ? local + '@' + EMAIL_DOMAIN : '';
};

// "Legacy" = created before the wizard was first switched on. Fail-closed:
// a missing `onboardingWizardSince` means nobody is new. driverConvertedAt
// (stamped by SettingsScreen.convert) lets a customer who becomes a driver
// after launch be treated as new for the driver wizard — but ONLY for a
// first-time driver: a legacy driver who round-trips driver → customer →
// driver already carries driver data (licence, car type, TIN or a driver
// completion stamp) and must stay legacy, or the convert would re-gate and
// re-credit an old account.
export const isLegacyAccount = (p, s) => {
    const since = Number(s && s.onboardingWizardSince) || 0;
    const createdAt = Number(p && p.createdAt) || 0;
    const convertedAt = Number(p && p.driverConvertedAt) || 0;
    const firstTimeDriver = convertedAt > 0 && !(p.licenseImage || p.carType || p.tin || p.onboardingRole === 'driver');
    const created = Math.max(createdAt, firstTimeDriver ? convertedAt : 0);
    return !since || !created || created < since;
};


// ---- Configurable wizard steps ------------------------------------------
// Every driver step group has a MODE the admin picks in the portal
// (/settings, flat keys `onboardingStep<Id>` = 'required' | 'optional' |
// 'off') and a position in ORDER (settings.onboardingStepOrder, a comma-
// separated list of group ids). An absent or invalid key falls back to the
// legacy switch the step used to follow (license_image_required, carType_
// required, term_required, imageIdApproval, bank_fields, the two TIN flags),
// so an untouched portal keeps today's behaviour exactly.
//
// ONE resolver drives everything: the wizard (which screens exist, whether
// they can be skipped, in which order), the shared missingFields() predicate
// and the complete_onboarding Cloud Function. The server therefore never
// demands a field the wizard did not ask for and never waves through one the
// admin marked required.
//
// Modes: 'required' = shown, Continue needs it, the server rejects Finish
// without it; 'optional' = shown with "Skip for now", never validated;
// 'off' = not shown, never validated. 'name' is always required and pinned
// first; 'extras' (selfie / insurance tiles + Finish) is pinned last and is
// never more than optional.
export const ONBOARDING_STEP_IDS = ['license', 'ownership', 'idPhoto', 'vehicle', 'carPhoto', 'tin', 'bank', 'terms', 'extras'];
export const ONBOARDING_STEP_MODES = ['required', 'optional', 'off'];
export const DEFAULT_ONBOARDING_STEP_ORDER = ['name', 'license', 'ownership', 'idPhoto', 'vehicle', 'carPhoto', 'tin', 'bank', 'terms', 'extras'];
// Profile fields each group collects, in the order the wizard asks for them.
// vehicleModel is optional and never reported missing; it is listed so an
// older server payload still sorts into the vehicle block.
export const ONBOARDING_STEP_FIELDS = {
    name: ['firstName', 'lastName'],
    license: ['licenseImage'],
    ownership: ['licenseImageBack'],
    idPhoto: ['verifyIdImage'],
    vehicle: ['carType', 'vehicleNumber', 'vehicleMake', 'vehicleModel'],
    carPhoto: ['car_image'],
    tin: ['tin'],
    bank: ['bankName', 'bankAccount'],
    terms: ['term'],
    extras: [],
};

export const onboardingStepSettingKey = (id) => 'onboardingStep' + str(id).charAt(0).toUpperCase() + str(id).slice(1);

// What the step did before it became configurable (also the portal's
// "Default" option).
export const legacyStepMode = (id, settings) => {
    const s = settings || {};
    switch (id) {
        case 'license':
        case 'ownership':
            return s.license_image_required ? 'required' : 'off';
        case 'idPhoto':
            return s.imageIdApproval === true ? 'required' : 'off';
        case 'vehicle':
            return s.carType_required ? 'required' : 'off';
        case 'carPhoto':
            return s.carType_required ? 'optional' : 'off';
        case 'tin':
            if (s.tinRequiredForOnline === true) return 'required';
            return s.onboardingTinStepEnabled === true ? 'optional' : 'off';
        case 'bank':
            return s.bank_fields ? 'required' : 'off';
        case 'terms':
            return s.term_required ? 'required' : 'off';
        case 'extras':
            return 'optional';
        default:
            return 'off';
    }
};

// The portal value for a step, or null when unset / not a known mode.
export const configuredStepMode = (id, settings) => {
    const raw = settings ? settings[onboardingStepSettingKey(id)] : undefined;
    const v = str(raw).trim().toLowerCase();
    return ONBOARDING_STEP_MODES.indexOf(v) >= 0 ? v : null;
};

// Effective mode of every step group. Coherence rules no portal value can
// override: the TIN stays required while tinRequiredForOnline is on (the
// driver could not go online otherwise); the car photo is off while the
// vehicle block is off (there is no car record to attach it to); extras is
// never required.
export const resolveStepModes = (settings) => {
    const s = settings || {};
    const modes = {};
    ONBOARDING_STEP_IDS.forEach((id) => {
        modes[id] = configuredStepMode(id, s) || legacyStepMode(id, s);
    });
    if (s.tinRequiredForOnline === true) modes.tin = 'required';
    if (modes.vehicle === 'off') modes.carPhoto = 'off';
    if (modes.extras === 'required') modes.extras = 'optional';
    return modes;
};

// Group order for the driver wizard: settings.onboardingStepOrder (string
// "name,license,…" or an array), unknown ids dropped, duplicates collapsed,
// groups the admin left out appended in default order; 'name' pinned first,
// 'extras' pinned last, 'carPhoto' kept right after 'vehicle' (its steps
// draft the car the photo attaches to). Modes are NOT applied here — an
// 'off' group is simply skipped by the consumers.
export const resolveStepOrder = (settings) => {
    const s = settings || {};
    const raw = s.onboardingStepOrder;
    const parts = Array.isArray(raw) ? raw : str(raw).split(',');
    const seen = {};
    const picked = [];
    parts.forEach((x) => {
        const id = str(x).trim();
        if (DEFAULT_ONBOARDING_STEP_ORDER.indexOf(id) < 0 || seen[id]) return;
        seen[id] = true;
        picked.push(id);
    });
    DEFAULT_ONBOARDING_STEP_ORDER.forEach((id) => { if (!seen[id]) picked.push(id); });
    const middle = picked.filter((id) => id !== 'name' && id !== 'extras' && id !== 'carPhoto');
    middle.splice(middle.indexOf('vehicle') + 1, 0, 'carPhoto');
    return ['name'].concat(middle, ['extras']);
};

// Every profile field the wizard may report missing, in the configured
// order — the sort key for missingFields() and for the server's `first`.
export const orderedFieldList = (settings) => {
    const out = [];
    resolveStepOrder(settings).forEach((g) => {
        (ONBOARDING_STEP_FIELDS[g] || []).forEach((f) => out.push(f));
    });
    return out;
};

// Ordered list of missing/invalid required profile fields, in wizard step
// order — the FULL set a gated account completes inside the wizard.
// Which groups are validated comes from resolveStepModes() above (portal
// per-step modes, legacy switches as defaults). opts.requireTin is kept for
// older call sites; the resolver already forces the TIN while
// tinRequiredForOnline is on.
// The TIN is OPTIONAL in the wizard (owner decision 2026-09-05): it is asked
// on its own step, may be skipped, and is required — for new AND legacy
// accounts alike — only while tinRequiredForOnline is on. TIN never decides
// whether a legacy account is gated in the first place (see
// legacyMissingFields).
export const missingFields = (p, s, opts) => {
    const settings = s || {};
    const modes = resolveStepModes(settings);
    // opts.requireTin is honoured for older call sites; the resolver already
    // forces the TIN to 'required' while tinRequiredForOnline is on.
    const requireTin = modes.tin === 'required' || Boolean(opts && opts.requireTin);
    const m = {};
    if (!p) return ['firstName', 'lastName'];
    if (!isRealName(p.firstName)) m.firstName = true;
    if (!isRealName(p.lastName)) m.lastName = true;
    if (p.usertype === 'driver') {
        if (modes.license === 'required' && !p.licenseImage) m.licenseImage = true;
        if (modes.ownership === 'required' && !p.licenseImageBack) m.licenseImageBack = true;
        if (modes.idPhoto === 'required' && !p.verifyIdImage) m.verifyIdImage = true;
        if (modes.vehicle === 'required') {
            if (!p.carType) m.carType = true;
            if (!p.vehicleNumber) m.vehicleNumber = true;
            if (!p.vehicleMake) m.vehicleMake = true;
            // vehicleModel is OPTIONAL (owner decision): many drivers only know
            // the make; the model is collected via pills/free text when known.
        }
        if (modes.carPhoto === 'required' && !p.car_image) m.car_image = true;
        if (requireTin && !isValidTin(p.tin)) m.tin = true;
        if (modes.bank === 'required') {
            if (!trim(p.bankName)) m.bankName = true;
            if (!trim(p.bankAccount)) m.bankAccount = true;
        }
    }
    if (modes.terms === 'required' && p.term !== true) m.term = true;
    return orderedFieldList(settings).filter((f) => m[f] === true);
};

// OTP placeholder name pairs written by phone sign-up. A legacy account
// carrying one of these has never entered a name; any other non-empty pair
// ("Driver Test" included) was accepted for years and is not re-gated.
const LEGACY_PLACEHOLDER_PAIRS = ['manual customer', 'manual rider'];

const isLegacyPlaceholderPair = (first, last) =>
    LEGACY_PLACEHOLDER_PAIRS.indexOf((trim(first) + ' ' + trim(last)).toLowerCase()) >= 0;

// Legacy completeness == parity with TODAY's go-online gate (the DriverTrips
// blocking alerts + the customer terms banner), so an existing account that
// can already work is never pulled into the wizard. Ordered subset of the
// same field names as missingFields(): names only when empty/whitespace or
// the OTP placeholder pair (NOT the strict NAME_RE); only the FRONT licence;
// only the car TYPE (plate/make/model live on /cars for old accounts);
// terms; never 'tin'. Once gated, the wizard still collects the full
// missingFields() set for the missing groups — that is intended.
export const legacyMissingFields = (p, s) => {
    const settings = s || {};
    // Parity with the go-online gate (driverOnlineBlockers): only a group the
    // admin marked 'required' can pull a legacy account in — an 'optional' or
    // 'off' step never blocks going online, so it never gates either.
    const modes = resolveStepModes(settings);
    const m = {};
    if (!p) return ['firstName', 'lastName'];
    const placeholder = isLegacyPlaceholderPair(p.firstName, p.lastName);
    if (placeholder || !trim(p.firstName)) m.firstName = true;
    if (placeholder || !trim(p.lastName)) m.lastName = true;
    if (p.usertype === 'driver') {
        if (modes.license === 'required' && !p.licenseImage) m.licenseImage = true;
        if (modes.idPhoto === 'required' && !p.verifyIdImage) m.verifyIdImage = true;
        if (modes.vehicle === 'required' && !p.carType) m.carType = true;
    }
    if (modes.terms === 'required' && p.term !== true) m.term = true;
    return orderedFieldList(settings).filter((f) => m[f] === true);
};

// ---- Go-online gate = the step table ---------------------------------------
// ONE rule for the driver home switch, its red rows, the Done-step "Start
// driving", the accept-trip ID check and the server's dispatch eligibility
// (eligibilitySignature): a document blocks a driver only while its wizard
// group is 'required' in the portal table — the classic switches merely
// supply the defaults. Not wizard groups, kept as they were: the TIN blocks
// going online only through tinRequiredForOnline (a staged flag: banner
// first, then hard gate — a table 'required' alone means "asked in the
// wizard"); admin approvals follow driver_approval / carApproval. Ownership
// certificate, payout method and car photo never block going online (a
// legacy driver without them must keep working). Returns the blocker keys in
// the order the home screen shows them; empty = may go online.
export const driverOnlineBlockers = (p, s) => {
    const settings = s || {};
    const modes = resolveStepModes(settings);
    const m = [];
    if (!p) return m;
    if (settings.tinRequiredForOnline === true && !p.tin) m.push('tin');
    if (modes.license === 'required' && !p.licenseImage) m.push('licenseImage');
    if (modes.terms === 'required' && p.term !== true) m.push('term');
    if (modes.idPhoto === 'required' && !p.verifyIdImage) m.push('verifyIdImage');
    if (modes.vehicle === 'required') {
        if (!p.carType) m.push('carType');
        else if (settings.carApproval === true && p.carApproved !== true) m.push('carApproved');
    }
    // The approval gate (settings.approvalGateEnabled, off by default) also
    // needs approval to go online, whatever driver_approval says. Twin:
    // functions/common/onboarding.js driverOnlineBlockers.
    if ((settings.driver_approval || settings.approvalGateEnabled === true) && p.approved !== true) m.push('approved');
    return m;
};

// Placeholder car info stamped by the admin fill job (Security → "Fill
// Vehicle Placeholders"): carType only, plate left empty, so the driver can
// go online and be dispatched while the call center collects the real car.
// "Pending" until a real plate is saved — every real car writer (wizard,
// Cars screen, portal Cars page) mirrors the active car incl. vehicleNumber
// onto the profile, so the driver leaves the call list without any clearing
// logic. The flag itself stays as an audit stamp (vehiclePlaceholderAt/By).
export const vehiclePlaceholderPending = (p) =>
    !!p && p.vehiclePlaceholder === true && !String(p.vehicleNumber || '').trim();

// Accepting a trip needs an admin-verified ID (verifyId + verifyIdImage) only
// while the ID-photo step is 'required'.
export const driverIdVerifiedForTrip = (p, s) => {
    if (resolveStepModes(s || {}).idPhoto !== 'required') return true;
    return Boolean(p && p.verifyId && p.verifyIdImage);
};

// "Complete" == would not be gated. Legacy: today's go-online rules; new:
// the full wizard set (a valid TIN only while tinRequiredForOnline is on).
export const isProfileComplete = (p, s) => {
    if (!p) return false;
    if (isLegacyAccount(p, s)) return legacyMissingFields(p, s).length === 0;
    return missingFields(p, s, { requireTin: (s || {}).tinRequiredForOnline === true }).length === 0;
};


// ---- Re-onboarding of stamped drivers ---------------------------------------
// The completion stamp is final for everything EXCEPT this: a driver who
// finished the wizard while a step was hidden/optional and is later blocked
// (driverOnlineBlockers) by a step the admin made 'required' is guided back
// into the wizard — at that step only — instead of being left on the red
// rows (owner decision 2026-09-08). Only wizard-collectable blockers count:
// admin approvals (carApproved / approved) never reopen it, and steps that
// never block going online (ownership certificate, car photo, payout) never
// do either. Such a driver is already unable to go online, so no working
// driver is ever interrupted. Customers keep the stamp final (their map's
// terms banner handles a later terms requirement).
export const REONBOARDING_FIELDS = ['licenseImage', 'verifyIdImage', 'carType', 'term', 'tin'];

export const reonboardingFields = (p, s) =>
    driverOnlineBlockers(p, s).filter((k) => REONBOARDING_FIELDS.indexOf(k) >= 0);

// An account that switched role after sign-up: customer → driver leaves
// driverConvertedAt (SettingsScreen.convert); driver → customer leaves the
// driver's data behind on a customer record.
export const isConvertedAccount = (p) => {
    if (!p) return false;
    if ((Number(p.driverConvertedAt) || 0) > 0) return true;
    return p.usertype === 'customer' && Boolean(p.driverCode || p.licenseImage || p.carType || p.onboardingRole === 'driver');
};

export const hasOnboardingStamp = (p) =>
    Boolean(p && p.onboardingCompletedAt && p.onboardingRole === p.usertype);

// The navigator gate. True only when: the wizard flag is on (or the account
// is an admin-marked canary), the role has no completion stamp, and the
// profile is incomplete (any account) or the account is new (until stamped).
export const needsOnboarding = (p, s) => {
    if (!p || !p.uid) return false;
    if (p.usertype !== 'driver' && p.usertype !== 'customer') return false;
    const settings = s || {};
    const canary = p.onboardingCanary === true;
    if (settings.onboardingWizardEnabled !== true && !canary) return false;
    // Stamped: closed for good, unless a driver is now blocked by a required
    // step the wizard can collect (see reonboardingFields).
    if (hasOnboardingStamp(p)) return p.usertype === 'driver' && reonboardingFields(p, settings).length > 0;
    // Role conversions (Settings → Convert) are judged by COMPLETENESS only,
    // never by the "new account until stamped" rule (owner decision
    // 2026-09-05): a driver who becomes a customer already has a real name
    // and accepted terms, so nothing opens; a customer who becomes a driver
    // sees the wizard only while the driver essentials (licence, car type,
    // terms) are missing, and then only for what is missing.
    if (isConvertedAccount(p)) return legacyMissingFields(p, settings).length > 0;
    const legacy = isLegacyAccount(p, settings);
    // Smart gate: ANY incomplete profile is routed into the wizard, which
    // opens at the first missing step — this covers existing (legacy)
    // drivers who never uploaded a licence / registered a car / accepted
    // terms and today only see a stack of red banners. Legacy accounts are
    // judged by today's go-online rules (legacyMissingFields: front licence,
    // car type, terms, non-placeholder name; TIN is deliberately not part of
    // it), so working drivers are never bothered. A NEW account without a
    // stamp is gated even when every field is present: the server-side
    // completion (email binding, welcome credit, carApproved repair) has not
    // happened yet, so the wizard opens directly on its Finish step.
    if (legacy && isProfileComplete(p, settings)) return false;
    return true;
};
